// HR Policies (Nov 3, 2026).
//   • HrPoliciesTab — the HR Center tab: create a policy (name + documents),
//     send it to employees, and track who has / hasn't completed it.
//   • MyHrPoliciesCard — the employee's own profile card: read the documents,
//     tick "I have read and acknowledge", sign. The server enforces every rule.
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { ArrowLeft, FileText, Plus, Trash2 } from "lucide-react";
import {
  fetchHrPolicies, fetchHrPolicy, createHrPolicy, updateHrPolicy, deleteHrPolicy,
  uploadHrPolicyDocument, deleteHrPolicyDocument, assignHrPolicy, unassignHrPolicy,
  fetchMyHrPolicies, signMyHrPolicy,
} from "../api/hrPolicies";
import { DataTable } from "./DataTable";
import Modal from "./Modal";
import SignaturePad from "./SignaturePad";
import { PARTNER_ROLE_KEY, roleLabel } from "../constants/roles";
import { EMPTY_INCLUDE, filterEmployees, filterOptions } from "../utils/employeeFilter";
import { uploadFiles, alertUploadFailures } from "../utils/uploadFiles";
import { toast, apiErrorMessage } from "../utils/toast";
import { confirmDialog } from "../utils/confirm";
import {
  cardCls, inputCls, labelCls, sectionHeadingCls, buttonPrimaryCls, buttonSecondaryCls,
  buttonPrimarySmCls, buttonSecondarySmCls, buttonDangerSmCls, filterPillActiveCls, filterPillInactiveCls,
} from "../styles/ui";

const fmtDate = (iso) => {
  const d = iso ? DateTime.fromISO(iso) : null;
  return d?.isValid ? d.toFormat("MMM d, yyyy") : "—";
};

const DONE_BADGE = "bg-success-50 text-success-700 border-success-200";
const TODO_BADGE = "bg-warning-50 text-warning-700 border-warning-200";

function StatusBadge({ done }) {
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${done ? DONE_BADGE : TODO_BADGE}`}>
      {done ? "Completed" : "Not completed"}
    </span>
  );
}

function DocumentLinks({ documents, onDelete }) {
  if (!documents.length) return <p className="text-sm text-ink-muted">No documents.</p>;
  return (
    <ul className="space-y-1">
      {documents.map((d) => (
        <li key={d.id} className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-ink-muted shrink-0" aria-hidden="true" />
          <a href={d.storage_url} target="_blank" rel="noreferrer" className="text-sm text-brand-700 hover:text-brand-600 truncate">{d.file_name}</a>
          {onDelete && (
            <button type="button" aria-label={`Delete ${d.file_name}`} onClick={() => onDelete(d)}
              className="text-ink-muted hover:text-danger-600"><Trash2 className="w-4 h-4" aria-hidden="true" /></button>
          )}
        </li>
      ))}
    </ul>
  );
}

// Checkbox list over the employees: search, role/department/region filter
// buttons, and select-all-shown.
export function EmployeeChecklist({ users, selected, onChange }) {
  const [q, setQ] = useState("");
  const [include, setInclude] = useState(EMPTY_INCLUDE);
  // Options from every user, not the filtered ones, so a selected button never vanishes.
  const groups = useMemo(() => filterOptions(users), [users]);
  const shown = useMemo(() => filterEmployees(users, q, include), [users, q, include]);
  const filtersOn = Object.values(include).some((v) => v.size);
  const toggleFilter = (group, value) => setInclude((prev) => {
    const next = new Set(prev[group]);
    next.has(value) ? next.delete(value) : next.add(value);
    return { ...prev, [group]: next };
  });
  const allShown = shown.length > 0 && shown.every((u) => selected.has(u.id));
  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(next);
  };
  const toggleAll = () => {
    const next = new Set(selected);
    shown.forEach((u) => (allShown ? next.delete(u.id) : next.add(u.id)));
    onChange(next);
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <input className={inputCls} placeholder="Search employees…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className={`${buttonSecondarySmCls} whitespace-nowrap`} onClick={toggleAll} disabled={!shown.length}>
          {allShown ? "Clear shown" : "Select all shown"}
        </button>
      </div>
      {groups.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {groups.map(([key, label, options]) => (
            <div key={key} className="flex flex-wrap items-center gap-1">
              <span className="text-xs text-ink-muted w-20 shrink-0">{label}</span>
              {options.map((o) => {
                const on = include[key].has(o);
                return (
                  <button key={o} type="button" aria-pressed={on} onClick={() => toggleFilter(key, o)}
                    className={on
                      ? "px-2 py-0.5 rounded text-xs bg-brand-600 text-white"
                      : "px-2 py-0.5 rounded text-xs bg-surface-inset text-ink-muted border border-line hover:bg-zinc-200"}>
                    {key === "role" ? roleLabel(o) : o}
                  </button>
                );
              })}
            </div>
          ))}
          {filtersOn && (
            <button type="button" className="text-xs text-brand-700 hover:text-brand-600" onClick={() => setInclude(EMPTY_INCLUDE)}>
              Clear filters
            </button>
          )}
        </div>
      )}
      <div className="border border-line rounded-md max-h-64 overflow-y-auto divide-y divide-line">
        {shown.length === 0 && <p className="text-sm text-ink-muted p-3">No employees.</p>}
        {shown.map((u) => (
          <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm text-ink cursor-pointer hover:bg-surface-inset">
            <input type="checkbox" className="accent-brand-600" checked={selected.has(u.id)} onChange={() => toggle(u.id)} />
            {u.name || u.email}
          </label>
        ))}
      </div>
      <p className="text-xs text-ink-muted mt-1">{selected.size} selected{filtersOn || q ? ` · ${shown.length} shown` : ""}</p>
    </div>
  );
}

function NewPolicyModal({ users, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) return toast.error("Enter a policy name.");
    if (!files.length) return toast.error("Add at least one document.");
    setSaving(true);
    try {
      const policy = await createHrPolicy(name.trim());
      const { results, failed } = await uploadFiles(files, (f) => uploadHrPolicyDocument(policy.id, f));
      alertUploadFailures(failed);
      // Only send once a document actually landed — the server refuses otherwise.
      if (results.length && selected.size) {
        await assignHrPolicy(policy.id, [...selected]);
        toast.success(`Sent to ${selected.size} employee${selected.size === 1 ? "" : "s"}.`);
      } else if (selected.size) {
        toast.warning("Policy saved but not sent — no document uploaded. Add one on the policy's page, then send it.");
      }
      onCreated(policy.id);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not create the policy"));
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title="New HR policy" closeOnEscape={false} closeOnBackdrop={false}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Policy name</label>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} autoFocus />
        </div>
        <div>
          <label className={labelCls}>Documents</label>
          <input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))}
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.tif,.tiff" className="text-sm" />
          {files.length > 0 && <p className="text-xs text-ink-muted mt-1">{files.map((f) => f.name).join(", ")}</p>}
        </div>
        <div>
          <label className={labelCls}>Send to</label>
          <EmployeeChecklist users={users} selected={selected} onChange={setSelected} />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className={buttonSecondaryCls} onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className={buttonPrimaryCls} disabled={saving}>
            {saving ? "Saving…" : selected.size ? "Create & Send" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SendModal({ users, onClose, onSend }) {
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  async function send() {
    setSaving(true);
    try {
      await onSend([...selected]);
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not send the policy"));
      setSaving(false);
    }
  }
  return (
    <Modal onClose={onClose} title="Send to employees">
      <EmployeeChecklist users={users} selected={selected} onChange={setSelected} />
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" className={buttonSecondaryCls} onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className={buttonPrimaryCls} onClick={send} disabled={saving || !selected.size}>
          {saving ? "Sending…" : `Send to ${selected.size}`}
        </button>
      </div>
    </Modal>
  );
}

function PolicyDetail({ policyId, users, onBack }) {
  const qc = useQueryClient();
  const key = ["hrPolicy", policyId];
  const { data: policy, isLoading } = useQuery({ queryKey: key, queryFn: () => fetchHrPolicy(policyId), staleTime: 0 });
  const [filter, setFilter] = useState("all");
  const [sending, setSending] = useState(false);
  const [editName, setEditName] = useState(null);
  const [busy, setBusy] = useState(false);

  const apply = (updated) => {
    qc.setQueryData(key, updated);
    qc.invalidateQueries({ queryKey: ["hrPolicies"] });
  };
  const run = async (fn, msg) => {
    setBusy(true);
    try { apply(await fn()); } catch (err) { toast.error(apiErrorMessage(err, msg)); } finally { setBusy(false); }
  };

  if (isLoading || !policy) return <p className="text-sm text-ink-muted">Loading…</p>;

  const assignedIds = new Set(policy.assignments.map((a) => a.user_id));
  const unassigned = users.filter((u) => !assignedIds.has(u.id));
  const rows = policy.assignments.filter((a) => filter === "all" || (filter === "done") === a.completed);

  async function addDocs(fileList) {
    setBusy(true);
    const { results, failed } = await uploadFiles(fileList, (f) => uploadHrPolicyDocument(policy.id, f));
    alertUploadFailures(failed);
    if (results.length) apply(results[results.length - 1]);
    setBusy(false);
  }

  async function removeDoc(d) {
    if (!(await confirmDialog({ title: `Delete ${d.file_name}?`, message: "Employees will no longer see this document.", confirmLabel: "Delete", danger: true }))) return;
    run(() => deleteHrPolicyDocument(policy.id, d.id), "Could not delete the document");
  }

  async function removeAssignment(a) {
    if (!(await confirmDialog({
      title: `Remove ${a.user_name}?`,
      message: a.completed ? "Their acknowledgement and signature will be deleted." : "They will no longer be asked to complete this policy.",
      confirmLabel: "Remove", danger: true,
    }))) return;
    run(() => unassignHrPolicy(policy.id, a.id), "Could not remove the employee");
  }

  async function removePolicy() {
    if (!(await confirmDialog({
      title: `Delete ${policy.name}?`,
      message: "This deletes the policy, its documents, and every employee's acknowledgement and signature.",
      confirmLabel: "Delete", danger: true,
    }))) return;
    try {
      await deleteHrPolicy(policy.id);
      qc.invalidateQueries({ queryKey: ["hrPolicies"] });
      onBack();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not delete the policy"));
    }
  }

  const columns = [
    { key: "person", label: "Employee", render: (a) => <>{a.user_name}{!a.user_active && <span className="text-xs text-ink-muted ml-1">(archived)</span>}</> },
    { key: "status", label: "Status", render: (a) => <StatusBadge done={a.completed} /> },
    { key: "sent", label: "Sent", render: (a) => fmtDate(a.assigned_at) },
    {
      key: "signed", label: "Signed",
      render: (a) => a.completed ? (
        <span className="flex items-center gap-2">
          {a.signature_url && <a href={a.signature_url} target="_blank" rel="noreferrer"><img src={a.signature_url} alt={`Signature of ${a.signed_name}`} className="h-8 bg-white border border-line rounded" /></a>}
          <span className="text-xs text-ink-muted">{a.signed_name} · {fmtDate(a.signed_at)}</span>
        </span>
      ) : "—",
    },
    {
      key: "remove", label: "",
      render: (a) => (
        <button type="button" aria-label={`Remove ${a.user_name}`} disabled={busy}
          onClick={(e) => { e.stopPropagation(); removeAssignment(a); }}
          className="text-ink-muted hover:text-danger-600"><Trash2 className="w-4 h-4" aria-hidden="true" /></button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-brand-700 hover:text-brand-600">
        <ArrowLeft className="w-4 h-4" aria-hidden="true" /> All policies
      </button>
      <div className={`${cardCls} p-5`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          {editName === null ? (
            <div>
              <h2 className="text-lg font-semibold text-ink">{policy.name}</h2>
              <p className="text-sm text-ink-muted">{policy.completed_count} of {policy.assigned_count} completed · created {fmtDate(policy.created_at)}</p>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input className={inputCls} value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={200} autoFocus />
              <button type="button" className={buttonPrimarySmCls} disabled={busy || !editName.trim()}
                onClick={() => run(() => updateHrPolicy(policy.id, editName.trim()), "Could not rename").then(() => setEditName(null))}>Save</button>
              <button type="button" className={buttonSecondarySmCls} onClick={() => setEditName(null)}>Cancel</button>
            </div>
          )}
          {editName === null && (
            <div className="flex gap-2">
              <button type="button" className={buttonSecondarySmCls} onClick={() => setEditName(policy.name)}>Rename</button>
              <button type="button" className={buttonDangerSmCls} onClick={removePolicy}>Delete</button>
            </div>
          )}
        </div>
        <div className="mt-4">
          <h3 className={`${sectionHeadingCls} mb-2`}>Documents</h3>
          <DocumentLinks documents={policy.documents} onDelete={removeDoc} />
          <label className={`${buttonSecondarySmCls} inline-flex items-center gap-1 mt-2 cursor-pointer`}>
            <Plus className="w-4 h-4" aria-hidden="true" /> Add documents
            <input type="file" multiple className="hidden" disabled={busy}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.tif,.tiff"
              onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) addDocs(f); }} />
          </label>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="flex gap-2 overflow-x-auto">
            {[["all", `All (${policy.assigned_count})`], ["todo", `Not completed (${policy.assigned_count - policy.completed_count})`], ["done", `Completed (${policy.completed_count})`]].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} className={`${filter === k ? filterPillActiveCls : filterPillInactiveCls} whitespace-nowrap`}>{label}</button>
            ))}
          </div>
          <button type="button" className={`${buttonPrimarySmCls} inline-flex items-center gap-1`} onClick={() => setSending(true)}
            disabled={!policy.documents.length} title={policy.documents.length ? undefined : "Add a document first"}>
            <Plus className="w-4 h-4" aria-hidden="true" /> Send to employees
          </button>
        </div>
        <div className={cardCls}>
          <DataTable columns={columns} rows={rows} rowKey={(a) => a.id} emptyText="Nobody here yet." />
        </div>
      </div>

      {sending && (
        <SendModal users={unassigned} onClose={() => setSending(false)}
          onSend={async (ids) => { apply(await assignHrPolicy(policy.id, ids)); toast.success(`Sent to ${ids.length} employee${ids.length === 1 ? "" : "s"}.`); }} />
      )}
    </div>
  );
}

export function HrPoliciesTab({ users }) {
  const { data: policies = [], isLoading } = useQuery({ queryKey: ["hrPolicies"], queryFn: fetchHrPolicies, staleTime: 0 });
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  // Partner company accounts aren't employees.
  const employees = useMemo(() => users.filter((u) => u.role !== PARTNER_ROLE_KEY), [users]);

  if (selectedId) return <PolicyDetail policyId={selectedId} users={employees} onBack={() => setSelectedId(null)} />;

  const columns = [
    { key: "name", label: "Policy", render: (p) => p.name },
    { key: "docs", label: "Documents", render: (p) => p.document_count },
    {
      key: "done", label: "Completed",
      render: (p) => p.assigned_count ? (
        <span className={p.completed_count === p.assigned_count ? "text-success-700" : "text-ink"}>{p.completed_count} / {p.assigned_count}</span>
      ) : <span className="text-ink-muted">Not sent</span>,
    },
    { key: "created", label: "Created", render: (p) => fmtDate(p.created_at) },
  ];

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button type="button" className={buttonPrimarySmCls} onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4" aria-hidden="true" /> New policy
        </button>
      </div>
      <div className={cardCls}>
        <DataTable columns={columns} rows={policies} isLoading={isLoading} rowKey={(p) => p.id}
          emptyText="No HR policies yet." onRowClick={(p) => setSelectedId(p.id)} />
      </div>
      {creating && (
        <NewPolicyModal users={employees} onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); setSelectedId(id); }} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Employee: their own policies, on their profile
// ---------------------------------------------------------------------------

function SignPolicyModal({ policy, myName, onClose, onSigned }) {
  const [ack, setAck] = useState(false);
  const [name, setName] = useState(myName || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save(blob) {
    if (!ack) return setError("Tick the box to acknowledge you've read the policy.");
    if (!name.trim()) return setError("Enter your name.");
    setSaving(true);
    setError(null);
    try {
      await onSigned(name.trim(), blob);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save your signature"));
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title={policy.name}>
      <div className="space-y-4">
        <div>
          <h3 className={`${sectionHeadingCls} mb-2`}>Documents</h3>
          <DocumentLinks documents={policy.documents} />
        </div>
        {policy.completed ? (
          <div className="border border-line rounded-lg p-3">
            <p className="text-sm text-success-700 mb-2">Acknowledged and signed {fmtDate(policy.signed_at)}.</p>
            {policy.signature_url && <img src={policy.signature_url} alt="Your signature" className="h-16 bg-white border border-line rounded" />}
            <p className="text-xs text-ink-muted mt-1">{policy.signed_name}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="flex items-start gap-2 text-sm text-ink cursor-pointer">
              <input type="checkbox" className="accent-brand-600 mt-0.5" checked={ack} onChange={(e) => setAck(e.target.checked)} />
              I have read and acknowledge this policy.
            </label>
            <div>
              <label className={labelCls}>Your name</label>
              <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
            </div>
            <SignaturePad onSave={save} saving={saving} disabled={!ack} />
            {error && <p className="text-xs text-danger-700">{error}</p>}
          </div>
        )}
      </div>
    </Modal>
  );
}

export function MyHrPoliciesCard({ myName, openId: linkedId, onOpened }) {
  const qc = useQueryClient();
  const { data: policies = [], isSuccess } = useQuery({ queryKey: ["myHrPolicies"], queryFn: fetchMyHrPolicies });
  const [openId, setOpenId] = useState(null);
  // `?hrPolicy=<assignment id>` (the task and notification link) opens that one.
  useEffect(() => {
    if (!linkedId || !isSuccess) return;
    const id = Number(linkedId);
    if (policies.some((p) => p.id === id)) setOpenId(id);
    onOpened?.();
  }, [linkedId, isSuccess, policies, onOpened]);
  if (!policies.length) return null;
  const open = policies.find((p) => p.id === openId);
  const pending = policies.filter((p) => !p.completed).length;

  return (
    <div className={`${cardCls} p-5`}>
      <h3 className={sectionHeadingCls}>HR Policies</h3>
      {pending > 0 && (
        <p className="text-sm text-warning-700 mb-3">
          {pending === 1 ? "1 policy is" : `${pending} policies are`} waiting for your acknowledgement and signature.
        </p>
      )}
      <ul className="divide-y divide-line">
        {policies.map((p) => (
          <li key={p.id}>
            <button type="button" onClick={() => setOpenId(p.id)} className="w-full text-left py-2 flex items-center justify-between gap-3 hover:bg-surface-inset rounded px-2">
              <span className="min-w-0">
                <span className="block text-sm text-ink truncate">{p.name}</span>
                <span className="block text-xs text-ink-muted">Sent {fmtDate(p.assigned_at)}</span>
              </span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${p.completed ? DONE_BADGE : TODO_BADGE}`}>
                {p.completed ? "Completed" : "Read and sign"}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {open && (
        <SignPolicyModal key={open.id} policy={open} myName={myName} onClose={() => setOpenId(null)}
          onSigned={async (name, blob) => {
            await signMyHrPolicy(open.id, name, blob);
            qc.invalidateQueries({ queryKey: ["myHrPolicies"] });
            qc.invalidateQueries({ queryKey: ["tasks"] });
          }} />
      )}
    </div>
  );
}
